// Rewards program terms (migration 398) — the versioned numbers behind the
// program, plus the program switch and the default subscription trial.
// Spec: docs/superpowers/specs/2026-11-03-rewards-program-design.md → "Program terms"
//
// `referral.manage` reads and saves terms versions. The switch and the trial are
// whitelisted admin config keys written through PUT /admin/config/{key}.

import { apiUrl } from '../lib/api'

const BASE = '/admin/rewards/terms'

export const REWARDS_ENABLED_KEY = 'rewards.enabled'
export const TRIAL_DAYS_KEY = 'subscription.trial_days'

/** Every editable number, grouped the way the Settings section shows them. */
export const TERMS_GROUPS = [
  { id: 'entry', fields: ['entry_fee_usd', 'entry_credit_usd', 'entry_trial_days'] },
  { id: 'qualify', fields: ['qualify_min_days', 'qualify_min_shipped_orders'] },
  {
    id: 'rewards',
    fields: [
      'threshold_count',
      'threshold_reward_usd',
      'per_referral_reward_usd',
      'reward_credit_expiry_months',
    ],
  },
  {
    id: 'commission',
    fields: ['commission_percent', 'commission_period_days', 'conversion_bonus_percent'],
  },
] as const

export type TermsGroupId = (typeof TERMS_GROUPS)[number]['id']
export type TermsField = (typeof TERMS_GROUPS)[number]['fields'][number]
export const TERMS_FIELDS: TermsField[] = TERMS_GROUPS.flatMap((g) => [...g.fields])

/** How a field is entered and shown: dollars, a whole count, or a percent. */
export const FIELD_KIND: Record<TermsField, 'money' | 'count' | 'percent'> = {
  entry_fee_usd: 'money',
  entry_credit_usd: 'money',
  entry_trial_days: 'count',
  qualify_min_days: 'count',
  qualify_min_shipped_orders: 'count',
  threshold_count: 'count',
  threshold_reward_usd: 'money',
  per_referral_reward_usd: 'money',
  reward_credit_expiry_months: 'count',
  commission_percent: 'percent',
  commission_period_days: 'count',
  conversion_bonus_percent: 'percent',
}

/** Money and percents arrive as decimal strings, counts as numbers. */
export type TermsValues = Record<TermsField, string | number>

export interface TermsVersion extends TermsValues {
  id: string
  version: number
  note: string
  created_at: string
  created_by_email: string | null
}

export interface TermsOverview {
  current: TermsVersion
  versions: TermsVersion[]
  enabled: boolean
  default_trial_days: number
}

export class RewardsApiError extends Error {
  readonly status: number
  readonly code: string | null

  constructor(message: string, status: number, code: string | null) {
    super(message)
    this.status = status
    this.code = code
  }
}

async function readJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      detail?: { message?: string; code?: string } | string
    }
    const detail = typeof body.detail === 'object' ? body.detail : undefined
    throw new RewardsApiError(
      detail?.message ?? `HTTP ${res.status}`,
      res.status,
      detail?.code ?? null,
    )
  }
  return (await res.json()) as T
}

export const termsKey = ['admin', 'rewards', 'terms'] as const

export async function fetchTerms(): Promise<TermsOverview> {
  return readJson(await fetch(apiUrl(BASE)))
}

export async function saveTerms(values: TermsValues, note: string): Promise<TermsVersion> {
  const res = await fetch(apiUrl(BASE), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ...values, note }),
  })
  return readJson(res)
}

/** Write one whitelisted admin config value (the switch or the trial). */
export async function putConfig(
  key: typeof REWARDS_ENABLED_KEY | typeof TRIAL_DAYS_KEY,
  value: boolean | number,
): Promise<void> {
  const res = await fetch(apiUrl(`/admin/config/${encodeURIComponent(key)}`), {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      config_value: { value },
      value_type: typeof value === 'boolean' ? 'boolean' : 'numeric',
    }),
  })
  await readJson(res)
}
